import React from "react";
import {
  Card,
  Grid,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Map as MapIcon,
  MyLocation as MyLocationIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Update as UpdateIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CreateGLNVM from "../viewmodel/CreateGLNVM";

// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Map picker component
const MapPicker = ({ position, onPositionChange, height = 400 }) => {
  const LocationMarker = () => {
    useMapEvents({
      click(e) {
        onPositionChange([e.latlng.lat, e.latlng.lng]);
      },
    });

    return position === null ? null : (
      <Marker position={position}>
        <Popup>
          Selected Location<br />
          Lat: {position[0].toFixed(6)}<br />
          Lng: {position[1].toFixed(6)}
        </Popup>
      </Marker>
    );
  };

  return (
    <Box sx={{ height, width: '100%', border: '2px solid #e0e7ff', borderRadius: 2, overflow: 'hidden' }}>
      <MapContainer
        center={position || [20.5937, 78.9629]} // Center of India
        zoom={position ? 13 : 5}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <LocationMarker />
      </MapContainer>
    </Box>
  );
};

const CreateGLNView = ({ onGLNCreated, onCancel }) => {
  const {
    // State
    mapDialogOpen,
    mapPosition,
    validationResult,
    isValidating,
    formErrors,
    isSubmitting,
    showSuccess,
    isLoading,
    
    // Data
    countries,
    continents,
    regions,
    cities,
    formData,
    userProfile,
    builtCode,
    
    // Actions
    openMapDialog,
    closeMapDialog,
    handleMapConfirm,
    updateMapPosition,
    getCurrentLocation,
    handleSubmit,
    handleCancel,
    handleClearAll,
    performCodeValidation,
    enhancedFormChange,
    updateCoordinatesFromMap,
    extractSubregionFromUnlocode,
    
    // Computed values
    getValidationIconState,
    getCountryName,
    
    // Permissions
    canCreate,
  } = CreateGLNVM();

  // Handle form submission with callbacks
  const onSubmitClick = () => {
    handleSubmit(onGLNCreated, (error) => {
      console.error('Failed to create/update GLN:', error);
      // Error is already handled in the viewmodel with toast
    });
  };

  // Handle cancel with callback
  const onCancelClick = () => {
    handleCancel(onCancel);
  };

  // Handle current location with error handling
  const onGetCurrentLocation = async () => {
    try {
      await getCurrentLocation();
    } catch (error) {
      alert(error.message);
    }
  };

  // Get validation icon component
  const getValidationIcon = () => {
    const iconState = getValidationIconState();
    
    switch (iconState) {
      case 'loading':
        return <CircularProgress size={16} />;
      case 'success':
        return <CheckIcon sx={{ color: 'green' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'red' }} />;
      default:
        return null;
    }
  };

  // Handle GLN type checkbox changes
  const handleGlnTypeChange = (type, checked) => {
    const currentTypes = formData.gln_type ? formData.gln_type.split(',') : [];
    let newTypes;
    
    if (checked) {
      if (!currentTypes.includes(type)) {
        newTypes = [...currentTypes, type];
      } else {
        newTypes = currentTypes;
      }
    } else {
      newTypes = currentTypes.filter(t => t !== type);
    }
    
    enhancedFormChange('gln_type', newTypes.join(','));
  };

  // Determine if this is a create or update operation
  const isUpdateMode = formData.gln_id && formData.gln_id.trim() !== '';

  if (!canCreate()) {
    return (
      <Grid container spacing={3} sx={{ padding: "2em" }}>
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Access Denied</Typography>
            <Typography>
              You don't have permission to create GLN records. Please contact your administrator to request access.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    );
  }

  // Success message
  if (showSuccess) {
    return (
      <Grid container spacing={3} sx={{ padding: "2em" }}>
        <Grid item xs={12}>
          <Card 
            elevation={2} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              backgroundColor: '#f0fdf4',
              border: '2px solid #22c55e'
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} color="#22c55e" gutterBottom>
              GLN {formData.wasUpdated ? 'Updated' : 'Created'} Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your GLN record has been {formData.wasUpdated ? 'updated' : 'created'} and is now available in the system.
            </Typography>
            <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, mb: 3 }}>
              <Typography variant="body2" color="text.secondary">GLN ID</Typography>
              <Typography variant="h6" fontWeight={600} color="#22c55e">
                {formData.gln_id}
              </Typography>
            </Box>
            <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, mb: 3 }}>
              <Typography variant="body2" color="text.secondary">Generated Code</Typography>
              <Typography variant="h6" fontWeight={600} color="#22c55e">
                {builtCode}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                if (onGLNCreated) {
                  onGLNCreated(formData);
                } else {
                  window.location.reload();
                }
              }}
              startIcon={<ArrowBackIcon />}
              size="large"
              sx={{
                backgroundColor: '#22c55e',
                minWidth: 200,
                '&:hover': {
                  backgroundColor: '#16a34a'
                }
              }}
            >
              Back to GLN Records
            </Button>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <CBackDrop cOpen={isLoading} />
      <Grid container spacing={3} sx={{ padding: "2em" }}>
        
        {/* Header */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e7ff' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                backgroundColor: "#e0e7ff",
                color: "#4338ca"
              }}>
                <CodeIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600} sx={{ color: "#1f2937" }}>
                  Create New GLN Record
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fill in the information below to create a new Global Location Number
                </Typography>
                {isUpdateMode && (
                  <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Update Mode:</strong> GLN ID provided - existing record will be updated if found
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Stack>
          </Card>
        </Grid>

        {/* Single Form */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ p: 4, borderRadius: 2 }}>
            <Grid container spacing={3}>
              
              {/* Basic Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#1f2937', mb: 2 }}>
                  <CodeIcon sx={{ mr: 1 }} />
                  Basic Information
                </Typography>
              </Grid>

              {/* GLN ID Field - First and prominently placed */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GLN ID (Optional)"
                  value={formData.gln_id || ''}
                  onChange={(e) => {
                    // Only allow numeric input and limit to 13 digits
                    const value = e.target.value.replace(/\D/g, '').substring(0, 13);
                    enhancedFormChange('gln_id', value);
                  }}
                  error={!!formErrors.gln_id}
                  helperText={formErrors.gln_id || "Optional: Enter your own 13-digit GLN ID. If provided and exists, record will be updated instead of created."}
                  placeholder="e.g., 1234567890123"
                  inputProps={{ 
                    maxLength: 13, 
                    pattern: '[0-9]*',
                    inputMode: 'numeric'
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {isUpdateMode ? (
                          <Tooltip title="Update mode - this GLN will be updated if it exists">
                            <UpdateIcon sx={{ color: '#f59e0b' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Create mode - new GLN will be generated">
                            <AddIcon sx={{ color: '#10b981' }} />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: isUpdateMode ? '#f59e0b' : '#10b981',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: isUpdateMode ? '#f59e0b' : '#10b981',
                    },
                  }}
                />
              </Grid>

  <Grid item xs={12} md={6}>
  <FormControl fullWidth error={!!formErrors.code_type}>
    <InputLabel>Code Type*</InputLabel>
    <Select
      value={formData.code_type || ''}
      onChange={(e) => enhancedFormChange('code_type', e.target.value)}
      label="Code Type*"
    >
      <MenuItem value="BIC">BIC</MenuItem>
      <MenuItem value="IATA">IATA</MenuItem>
      <MenuItem value="Port Facility Code">Port Facility Code</MenuItem>
      <MenuItem value="UNLOCODE">UNLOCODE</MenuItem>
      <MenuItem value="SMDG">SMDG</MenuItem>
      <MenuItem value="OTHER">OTHER</MenuItem>
    </Select>
    {formErrors.code_type && <FormHelperText>{formErrors.code_type}</FormHelperText>}
  </FormControl>
</Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Code Suffix"
                  value={formData.code_suffix || ''}
                  onChange={(e) => enhancedFormChange('code_suffix', e.target.value.toUpperCase())}
                  error={!!formErrors.code_suffix}
                  helperText={formErrors.code_suffix || "Enter code suffix (e.g., GTICI, RTM, CVME)"}
                  placeholder="e.g., GTICI, RTM, CVME"
                />
              </Grid>

              {builtCode && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ backgroundColor: '#f0f9ff' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Generated Code Preview:</strong>
                    </Typography>
                    <Chip 
                      label={builtCode}
                      sx={{ 
                        fontSize: '1rem', 
                        fontWeight: 600, 
                        backgroundColor: '#dbeafe', 
                        color: '#1e40af' 
                      }} 
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                      {isUpdateMode 
                        ? 'This code will be used to update the existing GLN record'
                        : 'Note: A sequential number will be automatically added by the system for uniqueness (e.g., ' + builtCode + '_1, ' + builtCode + '_2, etc.)'
                      }
                    </Typography>
                    {process.env.NODE_ENV === 'development' && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                        Debug: C:{formData.continent} | Co:{formData.country} | R:{formData.region} | Ci:{formData.city} | S:{formData.code_suffix}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#047857' }}>
                  GLN Type
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.gln_type?.includes('digital') || false}
                        onChange={(e) => handleGlnTypeChange('digital', e.target.checked)}
                        sx={{ color: '#10b981' }}
                      />
                    }
                    label="Digital"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.gln_type?.includes('mobile') || false}
                        onChange={(e) => handleGlnTypeChange('mobile', e.target.checked)}
                        sx={{ color: '#10b981' }}
                      />
                    }
                    label="Mobile"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.gln_type?.includes('fixed_physical_location') || true}
                        onChange={(e) => handleGlnTypeChange('fixed_physical_location', e.target.checked)}
                        sx={{ color: '#10b981' }}
                      />
                    }
                    label="Fixed Physical Location"
                  />
                </FormGroup>
                <FormHelperText>Select applicable GLN types (Fixed Physical Location is default)</FormHelperText>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Facility Name"
                  value={formData.facility_name || ''}
                  onChange={(e) => enhancedFormChange('facility_name', e.target.value)}
                  placeholder="e.g., Second Marine Services"
                  helperText="Enter the name of the facility or organization"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Role Type</InputLabel>
                  <Select
                    value={formData.role_type || ''}
                    onChange={(e) => enhancedFormChange('role_type', e.target.value)}
                    label="Role Type"
                  >
                    <MenuItem value="">Select Role Type</MenuItem>
                    <MenuItem value="Seaport">Seaport</MenuItem>
                    <MenuItem value="Rail Terminal">Rail Terminal</MenuItem>
                    <MenuItem value="Road Terminal">Road Terminal</MenuItem>
                    <MenuItem value="Airport">Airport</MenuItem>
                    <MenuItem value="Postal Exchange">Postal Exchange</MenuItem>
                    <MenuItem value="Intermodal Terminal">Intermodal Terminal</MenuItem>
                    <MenuItem value="Fixed Transport Function">Fixed Transport Function</MenuItem>
                    <MenuItem value="Border Crossing Function">Border Crossing Function</MenuItem>
                  </Select>
                  <FormHelperText>
                    Optional: Specify the primary function of this location
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Location Details Section */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#1f2937', mb: 2 }}>
                  <LocationOnIcon sx={{ mr: 1 }} />
                  Location Details
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!formErrors.country}>
                  <InputLabel>Country*</InputLabel>
                  <Select
                    value={formData.country || ''}
                    onChange={(e) => enhancedFormChange('country', e.target.value)}
                    label="Country*"
                  >
                    <MenuItem value="">
                      <em>Select Country</em>
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name} ({country.code})
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.country && <FormHelperText>{formErrors.country}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Region/State</InputLabel>
                  <Select
                    value={formData.region || ''}
                    onChange={(e) => enhancedFormChange('region', e.target.value)}
                    label="Region/State"
                    disabled={!formData.country}
                  >
                    <MenuItem value="">
                      <em>Select Region/State</em>
                    </MenuItem>
                    {regions.map((region) => (
                      <MenuItem key={region.regionCode || region.region_code} value={region.regionCode || region.region_code}>
                        {region.regionName || region.region_name} ({region.regionCode || region.region_code})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the region/state for more precise code generation
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>City/Subregion</InputLabel>
                  <Select
                    value={formData.city || ''}
                    onChange={(e) => enhancedFormChange('city', e.target.value)}
                    label="City/Subregion"
                    disabled={!formData.region}
                  >
                    <MenuItem value="">
                      <em>Select City/Subregion</em>
                    </MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city.cityCode || city.city_code} value={city.cityCode || city.city_code}>
                        {city.cityName || city.city_name} ({city.cityCode || city.city_code})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the city/subregion for the most precise code
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Link to UN/LOCODE (Optional)"
                  value={formData.unlocode || ''}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    enhancedFormChange('unlocode', value);
                    extractSubregionFromUnlocode(value);
                  }}
                  placeholder="e.g., INMUN, USLAX"
                  helperText="Optional: Enter the UN/LOCODE - subregion will be auto-extracted if available"
                />
              </Grid>

              {/* Geographic Coordinates Section */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#1f2937', mb: 2 }}>
                  <MapIcon sx={{ mr: 1 }} />
                  Geographic Coordinates
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={formData.latitude || ''}
                  onChange={(e) => enhancedFormChange('latitude', e.target.value)}
                  error={!!formErrors.latitude}
                  helperText={formErrors.latitude || "Decimal degrees (-90 to 90)"}
                  inputProps={{ step: 'any', min: -90, max: 90 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={formData.longitude || ''}
                  onChange={(e) => enhancedFormChange('longitude', e.target.value)}
                  error={!!formErrors.longitude}
                  helperText={formErrors.longitude || "Decimal degrees (-180 to 180)"}
                  inputProps={{ step: 'any', min: -180, max: 180 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack spacing={1} sx={{ height: '100%', justifyContent: 'center' }}>
                  <Tooltip title="Pick location on interactive map">
                    <Button
                      variant="outlined"
                      startIcon={<MapIcon />}
                      onClick={openMapDialog}
                      fullWidth
                      size="large"
                    >
                      Pick on Map
                    </Button>
                  </Tooltip>
                  <Tooltip title="Use your current GPS location">
                    <Button
                      variant="outlined"
                      startIcon={<MyLocationIcon />}
                      onClick={onGetCurrentLocation}
                      fullWidth
                      size="large"
                    >
                      Current Location
                    </Button>
                  </Tooltip>
                </Stack>
              </Grid>

              {(formData.latitude && formData.longitude) && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Location Preview
                  </Typography>
                  <MapPicker
                    position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                    onPositionChange={updateCoordinatesFromMap}
                    height={300}
                  />
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
                    <Typography variant="body2" color="primary">
                      <LocationOnIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      <strong>Selected Coordinates:</strong> {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                    </Typography>
                  </Box>
                </Grid>
              )}

            </Grid>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={onCancelClick}
                  startIcon={<CancelIcon />}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  size="large"
                >
                  Clear All
                </Button>
              </Stack>

              <Button
                variant="contained"
                onClick={onSubmitClick}
                disabled={isSubmitting || !formData.code_type || !formData.country || !builtCode}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} />
                  ) : isUpdateMode ? (
                    <UpdateIcon />
                  ) : (
                    <SaveIcon />
                  )
                }
                size="large"
                sx={{
                  backgroundColor: isUpdateMode ? '#f59e0b' : '#10b981',
                  minWidth: 200,
                  '&:hover': {
                    backgroundColor: isUpdateMode ? '#d97706' : '#059669'
                  }
                }}
              >
                {isSubmitting 
                  ? (isUpdateMode ? 'Processing...' : 'Creating GLN...') 
                  : (isUpdateMode ? 'Create/Update GLN' : 'Create GLN')
                }
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Map Selection Dialog */}
      <Dialog
        open={mapDialogOpen}
        onClose={closeMapDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MapIcon />
            <Typography variant="h6">Select Location on Map</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click anywhere on the map to select the precise coordinates for your GLN location. 
            You can zoom in/out and pan around to find the exact location.
          </Typography>
          
          <MapPicker
            position={mapPosition}
            onPositionChange={updateMapPosition}
            height={500}
          />
          
          {mapPosition && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
              <Typography variant="body2" color="primary">
                <LocationOnIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                <strong>Selected Coordinates:</strong><br />
                Latitude: {mapPosition[0].toFixed(6)}<br />
                Longitude: {mapPosition[1].toFixed(6)}
              </Typography>
            </Box>
          )}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>Tip:</strong> For better accuracy, zoom in to see more details before selecting your location. 
              Major landmarks and buildings are visible at higher zoom levels.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMapDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={onGetCurrentLocation}
            startIcon={<MyLocationIcon />}
            variant="outlined"
          >
            Use My Location
          </Button>
          <Button 
            onClick={handleMapConfirm}
            startIcon={<CheckIcon />}
            variant="contained"
            disabled={!mapPosition}
            sx={{
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669'
              }
            }}
          >
            Confirm Location
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateGLNView;